/**
 * MeCell Maps - Main Entry Point
 *
 * Interactive visualization of the human cell for educational purposes.
 * This is the entry point that initializes the map and UI components.
 */

// Import OpenLayers CSS first
import 'ol/ol.css';

// Import application styles
import './styles/main.css';
import './styles/toolbar.css';
import './styles/search.css';
import './styles/tree.css';
import './styles/sidepanel.css';
import './styles/breadcrumb.css';
import './styles/notifications.css';
import './styles/popup.css';
import './styles/tour.css';
import './styles/legal.css';

// Import map and UI components
import { CellMap } from './map/CellMap.js';
import { Toolbar } from './ui/Toolbar.js';
import { SearchField } from './ui/SearchField.js';
import { TreeView } from './ui/TreeView.js';
import { Sidepanel } from './ui/Sidepanel.js';
import { Breadcrumb } from './ui/Breadcrumb.js';
import { Popup } from './ui/Popup.js';
import { Footer } from './ui/Footer.js';
import { Router } from './router.js';
import { getNotifications } from './ui/Notifications.js';
import { getSearchIndex } from './data/search.js';
import { announce } from './utils/accessibility.ts';
import { initErrorTracking, setupGlobalErrorHandler, captureException } from './utils/errorTracking.ts';
import { initPerformanceMonitoring, trackMapMetrics, trackLongTasks, markPerformance } from './utils/performanceMonitoring.ts';
// Tour is lazy-loaded to reduce initial bundle size

// Application state
const state = {
  map: null,
  structures: null,
  kebLayer: null,
  kebs: null,
  searchIndex: null,
  selectedStructure: null,
  toolbar: null,
  searchField: null,
  treeView: null,
  sidepanel: null,
  breadcrumb: null,
  popup: null,
  footer: null,
  router: null,
  notifications: null,
  tour: null
};

/**
 * Initialize the application
 */
async function init() {
  console.info('MeCell Maps v2.0.0 - Initializing...');

  // Initialize error tracking first (catches all errors)
  initErrorTracking();
  setupGlobalErrorHandler();

  // Initialize performance monitoring (Core Web Vitals)
  initPerformanceMonitoring();
  trackLongTasks();
  trackMapMetrics();
  markPerformance('app-init-start');

  // Initialize notifications (for user-facing error messages)
  state.notifications = getNotifications();

  // Set up offline detection
  setupOfflineDetection();

  try {
    // Initialize the map
    state.map = new CellMap('map-container');
    console.info('Map initialized');
  } catch (error) {
    console.error('Map initialization failed:', error);
    captureException(error, { tags: { component: 'map-init' } });
    state.notifications.error('Karte konnte nicht geladen werden. Bitte Seite neu laden.', {
      action: () => window.location.reload(),
      actionLabel: 'Neu laden'
    });
    return;
  }

  try {
    // Load structures for hit detection
    state.structures = await state.map.initStructures();
    console.info(`Structures loaded: ${state.structures.getStructureCount()} items`);

    // Load KEB markers
    state.kebLayer = await state.map.initKebs();
    console.info(`KEBs loaded: ${state.kebLayer.getKebCount()} markers`);
  } catch (error) {
    console.error('Data load failed:', error);
    captureException(error, { tags: { component: 'data-load' } });
    state.notifications.error('Daten konnten nicht geladen werden.', {
      action: async () => {
        state.notifications.info('Versuche erneut...');
        try {
          state.structures = await state.map.initStructures();
          state.kebLayer = await state.map.initKebs();
          state.notifications.success('Daten erfolgreich geladen!');
          await initializeSearchIndex();
          initializeInteractivity();
        } catch (retryError) {
          console.error('Retry failed:', retryError);
          state.notifications.error('Laden fehlgeschlagen. Bitte später erneut versuchen.');
        }
      },
      actionLabel: 'Erneut versuchen'
    });
    return;
  }

  // Initialize search index
  await initializeSearchIndex();

  // Initialize interactivity after data is loaded
  initializeInteractivity();

  // Remove loading message
  const loadingMessage = document.querySelector('.loading-message');
  if (loadingMessage) {
    loadingMessage.remove();
  }

  // Mark app initialization complete
  markPerformance('app-init-end');
  window.dispatchEvent(new CustomEvent('map:ready'));

  console.info('MeCell Maps initialized successfully');
}

/**
 * Initialize search index with structures and KEBs
 */
async function initializeSearchIndex() {
  try {
    // Load KEBs data
    const kebsResponse = await fetch('/data/kebs.json');
    if (!kebsResponse.ok) {
      throw new Error(`Failed to load KEBs: ${kebsResponse.status}`);
    }
    const kebsData = await kebsResponse.json();
    state.kebs = kebsData.kebs;
    console.info(`KEBs loaded: ${state.kebs.length} items`);

    // Initialize search index
    state.searchIndex = getSearchIndex();
    state.searchIndex.init(
      state.structures.getAllStructures(),
      state.kebs
    );
  } catch (error) {
    console.warn('Search index initialization failed:', error);
    // Non-fatal: search will just be unavailable
  }
}

/**
 * Initialize interactive components after data is loaded
 */
function initializeInteractivity() {
  // Initialize click handler for structure selection
  state.map.initClickHandler();

  // Initialize toolbar with search
  state.toolbar = new Toolbar('app');

  // Initialize search field (only if search index is ready)
  if (state.searchIndex && state.searchIndex.isReady()) {
    state.searchField = new SearchField(
      state.toolbar.getSearchContainer(),
      state.map
    );

    // Keyboard shortcut: Ctrl/Cmd+K to focus search
    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        state.searchField.focus();
      }
    });
  }

  // Initialize tree view for hierarchical navigation
  state.treeView = new TreeView('app', state.map);
  state.treeView.init();

  // Initialize sidepanel for Wikipedia content
  state.sidepanel = new Sidepanel('app');

  // Initialize breadcrumb for hierarchy navigation
  state.breadcrumb = new Breadcrumb('app', state.map);

  // Initialize popup for KEB information display
  state.popup = new Popup(state.map);

  // Initialize footer with legal page links
  state.footer = new Footer('app');

  // Initialize router for URL-based navigation
  state.router = new Router(state.map);

  // Pass notifications to router for error messages
  state.router.setNotifications(state.notifications);

  // Set up selection event listeners
  state.map.onStructureSelect((structure) => {
    state.selectedStructure = structure;
    console.info(`Selected: ${structure.name}`);
    console.info(`Ancestors: ${structure.ancestorTree.map((s) => s.name).join(' > ')}`);

    // Announce to screen readers
    announce(`${structure.name} ausgewählt`);

    // Close KEB popup when selecting a structure
    state.popup.close();

    // Update breadcrumb navigation
    state.breadcrumb.update(structure.ancestorTree);

    // Update tree view selection
    state.treeView.setSelected(structure.id);

    // Update URL
    state.router.onStructureSelect(structure);

    // Zoom to structure with animation
    state.map.zoomToStructure(structure);

    // Show Wikipedia panel
    state.sidepanel.show(structure);
  });

  // Handle KEB clicks
  state.map.onKebSelect(({ keb, coordinate }) => {
    console.info(`KEB selected: ${keb.name} (${keb.type})`);
    // Announce to screen readers
    announce(`${keb.name} - Wissensbutton geöffnet`);
    state.popup.show(keb, coordinate);
  });

  state.map.onStructureDeselect(() => {
    state.selectedStructure = null;
    console.info('Selection cleared');

    // Clear breadcrumb
    state.breadcrumb.clear();

    // Clear tree view selection
    state.treeView.setSelected(null);

    // Clear URL
    state.router.onStructureDeselect();

    // Close sidepanel
    state.sidepanel.close();
  });

  // Initialize router last (may trigger selection from URL)
  state.router.init();

  // Lazy load and initialize guided tour for first-time visitors
  initializeTour();

  // Expose for debugging in development
  if (import.meta.env.DEV) {
    window.mecell = {
      map: state.map,
      structures: state.structures,
      kebLayer: state.kebLayer,
      clickHandler: state.map.getClickHandler(),
      toolbar: state.toolbar,
      searchField: state.searchField,
      treeView: state.treeView,
      sidepanel: state.sidepanel,
      breadcrumb: state.breadcrumb,
      popup: state.popup,
      footer: state.footer,
      router: state.router,
      notifications: state.notifications,
      searchIndex: state.searchIndex,
      tour: state.tour,
      state: state
    };
    console.info('Debug: window.mecell available');
  }

  // Expose search index globally for search UI
  if (state.searchIndex) {
    window.searchIndex = state.searchIndex;
  }
}

/**
 * Lazy load and initialize the guided tour
 * Reduces initial bundle size by loading tour only when needed
 */
async function initializeTour() {
  try {
    const { getTour } = await import('./ui/Tour.js');
    state.tour = getTour();

    // Auto-start tour for first-time visitors (after a short delay)
    if (state.tour.shouldAutoStart()) {
      setTimeout(() => {
        state.tour.start();
      }, 1000);
    }
  } catch (error) {
    console.warn('Tour initialization failed:', error);
    // Non-fatal: tour is optional enhancement
  }
}

/**
 * Set up offline/online detection
 */
function setupOfflineDetection() {
  window.addEventListener('offline', () => {
    state.notifications.warning('Keine Internetverbindung. Einige Funktionen sind möglicherweise eingeschränkt.');
  });

  window.addEventListener('online', () => {
    state.notifications.success('Verbindung wiederhergestellt.');
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for testing
export { init, state };
